import { Link, createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Button, Field, FormError, Input } from "../components/ui";
import { AuthLayout } from "../features/auth/auth-layout";
import { authClient } from "../lib/auth-client";

const MIN_LENGTH = 12;

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    ...(typeof search.token === "string" && { token: search.token }),
    ...(typeof search.error === "string" && { error: search.error }),
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const { token, error: linkError } = Route.useSearch();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  if (!token || linkError) {
    return (
      <AuthLayout
        title="Link expired"
        footer={<Link to="/forgot-password">Request a new link</Link>}
      >
        <p className="text-sm text-muted">This password reset link is invalid or has expired.</p>
      </AuthLayout>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_LENGTH) return setError(`Use at least ${MIN_LENGTH} characters.`);
    if (password !== confirm) return setError("The passwords don't match.");
    setPending(true);
    setError(undefined);
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });
    setPending(false);
    if (resetError) setError(resetError.message ?? "This link is invalid or has expired.");
    else setDone(true);
  };

  return (
    <AuthLayout title="Choose a new password" footer={<Link to="/sign-in">Back to sign in</Link>}>
      {done ? (
        <output className="flex flex-col gap-4 text-sm text-muted">
          <p>Your password has been changed and you've been signed out everywhere.</p>
          <Button asChild>
            <Link to="/sign-in">Sign in</Link>
          </Button>
        </output>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
          <Field
            label="New password"
            hint={`At least ${MIN_LENGTH} characters. Avoid passwords you use elsewhere.`}
          >
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          <FormError message={error} />
          <Button type="submit" loading={pending}>
            Change password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
